import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StockMovementsService, StockMovement } from '../../services/stock-movements.service';

export interface StockMovementDialogData {
  title: string;
  stockMovement?: StockMovement;
}

@Component({
  selector: 'app-stock-movement-dialog',
  templateUrl: './stock-movement-dialog.component.html',
  styleUrls: ['./stock-movement-dialog.component.css']
})
export class StockMovementDialogComponent implements OnInit {
  stockMovementForm: FormGroup;
  isLoading = false;
  isEdit = false;
  movementTypes = ['IN', 'OUT'];
  locations = ['Warehouse A', 'Warehouse B', 'Warehouse C', 'Main Store', 'Backup Storage'];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<StockMovementDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: StockMovementDialogData,
    private stockMovementsService: StockMovementsService,
    private snackBar: MatSnackBar
  ) {
    this.stockMovementForm = this.createForm();
    this.isEdit = !!data.stockMovement;
  }

  ngOnInit(): void {
    if (this.data.stockMovement) {
      this.stockMovementForm.patchValue(this.data.stockMovement);
    }
    
    // Update total value when quantity or unit price changes
    this.stockMovementForm.get('quantity')?.valueChanges.subscribe(() => this.updateTotalValue());
    this.stockMovementForm.get('unitPrice')?.valueChanges.subscribe(() => this.updateTotalValue());
  }

  createForm(): FormGroup {
    return this.fb.group({
      id: [null],
      productCode: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(20)]],
      productName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      movementType: ['IN', [Validators.required]],
      quantity: [1, [Validators.required, Validators.min(1), Validators.max(10000)]],
      unitPrice: [0, [Validators.required, Validators.min(0.01), Validators.max(99999.99)]],
      referenceNumber: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
      movementDate: [new Date().toISOString().split('T')[0], [Validators.required]],
      location: ['', [Validators.required]],
      performedBy: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      notes: ['', [Validators.maxLength(500)]]
    });
  }

  get productCode() { return this.stockMovementForm.get('productCode'); }
  get productName() { return this.stockMovementForm.get('productName'); }
  get movementType() { return this.stockMovementForm.get('movementType'); }
  get quantity() { return this.stockMovementForm.get('quantity'); }
  get unitPrice() { return this.stockMovementForm.get('unitPrice'); }
  get referenceNumber() { return this.stockMovementForm.get('referenceNumber'); }
  get movementDate() { return this.stockMovementForm.get('movementDate'); }
  get location() { return this.stockMovementForm.get('location'); }
  get performedBy() { return this.stockMovementForm.get('performedBy'); }
  get notes() { return this.stockMovementForm.get('notes'); }

  updateTotalValue(): void {
    const quantity = this.stockMovementForm.get('quantity')?.value || 0;
    const unitPrice = this.stockMovementForm.get('unitPrice')?.value || 0;
    const totalValue = quantity * unitPrice;
    
    // Update the totalValue field in the form
    this.stockMovementForm.patchValue({ totalValue }, { emitEvent: false });
  }

  onSubmit(): void {
    if (this.stockMovementForm.invalid) {
      this.markFormGroupTouched(this.stockMovementForm);
      this.snackBar.open('Please fill in all required fields correctly', 'Close', { duration: 3000 });
      return;
    }

    this.isLoading = true;
    const stockMovement: StockMovement = {
      ...this.stockMovementForm.value,
      totalValue: this.calculateTotalValue()
    };

    if (this.isEdit) {
      this.updateStockMovement(stockMovement);
    } else {
      this.createStockMovement(stockMovement);
    }
  }

  createStockMovement(stockMovement: StockMovement): void {
    this.stockMovementsService.createStockMovement(stockMovement).subscribe({
      next: (response) => {
        if (response.success) {
          this.snackBar.open('Stock movement created successfully', 'Close', { duration: 3000 });
          this.dialogRef.close(response.data);
        } else {
          this.snackBar.open(response.message || 'Failed to create stock movement', 'Close', { duration: 3000 });
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error creating stock movement:', error);
        this.snackBar.open('Error creating stock movement', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  updateStockMovement(stockMovement: StockMovement): void {
    if (!stockMovement.id) return;

    this.stockMovementsService.updateStockMovement(stockMovement.id, stockMovement).subscribe({
      next: (response) => {
        if (response.success) {
          this.snackBar.open('Stock movement updated successfully', 'Close', { duration: 3000 });
          this.dialogRef.close(response.data);
        } else {
          this.snackBar.open(response.message || 'Failed to update stock movement', 'Close', { duration: 3000 });
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error updating stock movement:', error);
        this.snackBar.open('Error updating stock movement', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  calculateTotalValue(): number {
    const quantity = this.stockMovementForm.get('quantity')?.value || 0;
    const unitPrice = this.stockMovementForm.get('unitPrice')?.value || 0;
    return quantity * unitPrice;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getErrorMessage(field: any): string {
    if (field?.errors) {
      if (field.errors['required']) {
        return 'This field is required';
      }
      if (field.errors['minlength']) {
        return `Minimum length is ${field.errors['minlength'].requiredLength} characters`;
      }
      if (field.errors['maxlength']) {
        return `Maximum length is ${field.errors['maxlength'].requiredLength} characters`;
      }
      if (field.errors['min']) {
        return `Minimum value is ${field.errors['min'].min}`;
      }
      if (field.errors['max']) {
        return `Maximum value is ${field.errors['max'].max}`;
      }
    }
    return '';
  }
}
